import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { articles } from "@/mocks/carriers";
import { GUIDE_CHARACTER } from "@/assets/guide";

const SITE_NAME = "スマホ代見直し案内所";
const SITE_URL = "https://sumaho-minaoshi.com";
const AUTHOR_NAME = "スマホ代見直し案内所 編集部";
const DEFAULT_DESCRIPTION =
  "スマホ料金・端末の買い方・修理・乗り換えの注意点を整理。請求書チェックや料金診断で、自分に合う選択肢を分かりやすく案内します。";

const pages: Record<string, { title: string; description: string }> = {
  "/": {
    title: `${SITE_NAME}｜料金・端末・修理・乗り換えを整理`,
    description: DEFAULT_DESCRIPTION,
  },
  "/diagnosis": {
    title: `スマホ料金プラン診断｜${SITE_NAME}`,
    description: "データ使用量・通話・店頭サポートの希望から、比較候補となるスマホ料金プランを3つの質問で整理します。",
  },
  "/compare": {
    title: `スマホ料金プラン比較｜${SITE_NAME}`,
    description: "大手キャリア、サブブランド、オンライン専用プラン、格安SIMを、料金・データ容量・通話・サポートで比較します。",
  },
  "/bill-check": {
    title: `スマホ請求内訳チェック｜${SITE_NAME}`,
    description: "毎月のスマホ代を通信料・端末代・通話料・オプションに分け、見直す金額を整理できる無料ツールです。",
  },
  "/articles": {
    title: `スマホ料金・端末の記事一覧｜${SITE_NAME}`,
    description: "通信費、乗り換え、データ容量、iPhone購入・修理・中古端末について、確認する順番を分かりやすく解説します。",
  },
  "/about": {
    title: `この案内所について｜${SITE_NAME}`,
    description: "スマホ代見直し案内所の目的、情報の確認方法、運営情報をご案内します。",
  },
  "/policy": {
    title: `編集方針・免責事項｜${SITE_NAME}`,
    description: "スマホ代見直し案内所の比較基準、情報更新、広告・リンク、免責事項についてご説明します。",
  },
  "/privacy": {
    title: `プライバシーポリシー｜${SITE_NAME}`,
    description: "スマホ代見直し案内所における入力情報、外部サービス、アクセス解析・広告の取り扱いについてご説明します。",
  },
  "/contact": {
    title: `お問い合わせ｜${SITE_NAME}`,
    description: "掲載内容の修正依頼や、スマホ代見直し案内所へのお問い合わせはこちらからお送りください。",
  },
};

function setMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value));
}

function removeMeta(selector: string) {
  document.head.querySelector(selector)?.remove();
}

function toIsoDate(date: string) {
  return date.replaceAll(".", "-");
}

function setStructuredData(data: Record<string, unknown>) {
  let script = document.head.querySelector<HTMLScriptElement>('#site-structured-data');
  if (!script) {
    script = document.createElement("script");
    script.id = "site-structured-data";
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export default function SiteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const articleId = pathname.startsWith("/articles/") ? pathname.split("/")[2] : undefined;
    const article = articleId ? articles.find((item) => item.id === articleId) : undefined;
    const page = article
      ? { title: `${article.title}｜${SITE_NAME}`, description: article.excerpt }
      : pages[pathname] || {
          title: `ページが見つかりません｜${SITE_NAME}`,
          description: DEFAULT_DESCRIPTION,
        };
    const canonicalUrl = `${SITE_URL}${pathname === "/" ? "/" : pathname.replace(/\/$/, "")}`;
    const imageUrl = new URL(article?.image || GUIDE_CHARACTER.src, SITE_URL).href;

    document.title = page.title;
    setMeta('meta[name="description"]', { name: "description", content: page.description });
    setMeta('meta[property="og:title"]', { property: "og:title", content: page.title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: page.description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: article ? "article" : "website" });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    setMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
    setMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: article?.title || SITE_NAME });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: "ja_JP" });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: page.title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: page.description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });

    if (article) {
      const published = toIsoDate(article.date);
      setMeta('meta[property="article:published_time"]', { property: "article:published_time", content: published });
      setMeta('meta[property="article:modified_time"]', { property: "article:modified_time", content: published });
      setMeta('meta[property="article:section"]', { property: "article:section", content: article.category });
      setStructuredData({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Article",
            "@id": `${canonicalUrl}#article`,
            headline: article.title,
            description: article.excerpt,
            image: [imageUrl],
            datePublished: published,
            dateModified: published,
            articleSection: article.category,
            inLanguage: "ja-JP",
            mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
            author: { "@type": "Organization", name: AUTHOR_NAME, url: `${SITE_URL}/about` },
            publisher: { "@type": "Organization", name: SITE_NAME, url: `${SITE_URL}/` },
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${canonicalUrl}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "ホーム", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "記事一覧", item: `${SITE_URL}/articles` },
              { "@type": "ListItem", position: 3, name: article.title, item: canonicalUrl },
            ],
          },
        ],
      });
    } else {
      removeMeta('meta[property="article:published_time"]');
      removeMeta('meta[property="article:modified_time"]');
      removeMeta('meta[property="article:section"]');
      setStructuredData({
        "@context": "https://schema.org",
        "@type": pathname === "/" ? "WebSite" : "WebPage",
        name: page.title,
        description: page.description,
        url: canonicalUrl,
        inLanguage: "ja-JP",
      });
    }

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
  }, [pathname]);

  return null;
}
